import { useDebouncedValue } from "@/app/hooks/use-debounced-value"
import { useEvent } from "@/app/hooks/use-event"
import { clampNumber } from "@/app/utils/clamp-number"
import { InputNumber } from "@rarible_int/anaconda_ui_components"
import { useCallback, useEffect, useMemo, useState } from "react"

interface Props {
  defaultValue?: number
  onChange(value: number): void
  className?: string
  maxValue: number
  disabled?: boolean
  dataTestId?: string
}

const MIN_VALUE = 1

export function QuantityPicker({
  defaultValue,
  onChange,
  className,
  maxValue,
  disabled,
  dataTestId,
}: Props) {
  const [value, setValue] = useState<number | undefined>(
    defaultValue ?? MIN_VALUE,
  )
  const debouncedValue = useDebouncedValue(value, 600)

  const clampedValue = useMemo(() => {
    if (maxValue <= 1) {
      return 1
    }
    return clampNumber(value ?? MIN_VALUE, MIN_VALUE, maxValue)
  }, [value, maxValue])

  const handleChange = useCallback(
    (newValue: number) => {
      setValue(newValue)
      onChange(newValue)
    },
    [onChange],
  )

  useEffect(() => {
    if (debouncedValue === undefined) return
    handleChange(clampedValue)
  }, [debouncedValue, clampedValue, handleChange])

  const onInputChange = useEvent((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setValue(value === "" ? undefined : Number(value))
  })

  useEffect(() => {
    if (value === undefined) {
      setValue(MIN_VALUE)
      onChange(MIN_VALUE)
    }
  }, [value, onChange])

  useEffect(() => {
    if (maxValue <= 1 && value !== 1) {
      setValue(1)
      onChange(1)
    }
  }, [maxValue, value, onChange])

  const effectiveMaxValue = maxValue <= 1 ? 1 : maxValue
  const effectiveDisabled = disabled || maxValue <= 1

  return (
    <InputNumber
      value={value ?? ""}
      onChange={onInputChange}
      min={MIN_VALUE}
      max={effectiveMaxValue}
      step={1}
      className={className}
      disabled={effectiveDisabled}
      data-testid={dataTestId}
      id={dataTestId}
    />
  )
}
